import { useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { Button, Input, PasswordInput, TextInput } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { observer } from 'mobx-react-lite';
import { IMaskInput } from 'react-imask';
import { useMatch, useNavigate } from 'react-router-dom';
import { useTypedSearchParams } from 'react-router-typesafe-routes/dom';
import { Lock, Mail, MessageCode, User } from 'tabler-icons-react';

import { apiClient } from '../../core/libs/api';
import { HttpResource } from '../../core/libs/http-resource';
import { AppNotify } from '../../features/app-notify/app-notify';
import { routeCabinet, routeSearch, routeSignUp } from '../../router/routes';
import { authStore } from '../auth/auth.store';
import { useProfileStore } from '../profile/profile-provider';
import { signUpRequest } from './requests/sign-up.request';

export const SignUpForm = observer(() => {
  const [showCodeField, setShowCodeField] = useState(false);
  const [notify] = useState(() => new AppNotify());

  const [searchParams] = useTypedSearchParams(routeSearch);
  const [signUpParams] = useTypedSearchParams(routeSignUp);

  const navigate = useNavigate();
  const profileStore = useProfileStore();

  const signUpRoute = useMatch(routeSignUp);
  const orderTicketsRoute = useMatch(routeSearch);

  const handleNextRoute = (result: any) => {
    if (!result.session) {
      return notify.addMessage({
        type: 'error',
        message: t`Авторизация не удалась`,
      });
    }

    authStore.accessGranted();
    profileStore.setUserData(result);

    const cont = signUpParams.continue;

    if (cont === 'order-ticket' || orderTicketsRoute !== null) {
      const lowDate = searchParams.lowDate;
      const startStation = searchParams.startStation;
      const endStation = searchParams.endStation;
      const highDate = searchParams.highDate;

      const path = routeSearch.buildPath(
        {},
        {
          lowDate,
          startStation,
          endStation,
          highDate,
        }
      );

      return navigate(path);
    }

    if (signUpRoute === null) {
      window.location.reload();
      return;
    }

    return navigate(routeCabinet.path);
  };

  const [request] = useState(
    () =>
      new HttpResource({
        apiClient,
        httpRequest: signUpRequest,
        errorNotify: notify,
        onFinish(result: any) {
          setShowCodeField(true);

          if (result.user.phoneStatus === 1) {
            handleNextRoute(result);
          }
        },
      })
  );

  const form = useForm({
    initialValues: {
      phone: '',
      code: undefined,
      password: '',
      email: '',
    },

    validate: {
      phone: isNotEmpty(t`Пожалуйста, заполните поле`),
      password: isNotEmpty(t`Пожалуйста, заполните поле`),
      email: (value) => {
        if (!value || value.trim() === '') return null;

        if (!value.includes('@'))
          return t`Пожалуйста, проверьте правильность email`;

        return null;
      },
      code: (value) => {
        if (showCodeField === false) return null;
        if (!value) return t`Пожалуйста, заполните поле`;
      },
    },
  });

  return (
    <form onSubmit={form.onSubmit((values) => request.send(values))}>
      <Input.Wrapper
        label={<Trans>Номер телефона</Trans>}
        {...form.getInputProps('phone')}
      >
        <Input<any>
          size="lg"
          icon={<User size="1rem" />}
          component={IMaskInput}
          placeholder="374 01 234 567"
          mask="{374} 00 000 000"
          value={form.getInputProps('phone').value}
          onBlur={form.getInputProps('phone').onBlur}
          onFocus={form.getInputProps('phone').onFocus}
          error={form.getInputProps('phone').error}
          onAccept={(_: any, mask: { unmaskedValue: string }) => {
            form.getInputProps('phone').onChange(mask.unmaskedValue);
          }}
        />
      </Input.Wrapper>

      <PasswordInput
        size="lg"
        mt={8}
        label={<Trans>Пароль</Trans>}
        autoComplete="new-password"
        icon={<Lock size="1rem" />}
        {...form.getInputProps('password')}
      />

      <TextInput
        size="lg"
        mt={8}
        label="Email"
        autoComplete="email"
        icon={<Mail size="1rem" />}
        {...form.getInputProps('email')}
      />

      {showCodeField && (
        <TextInput
          size="lg"
          mt={8}
          label={<Trans>Код подтверждения</Trans>}
          autoComplete="one-time-code"
          inputMode="numeric"
          icon={<MessageCode size="1rem" />}
          {...form.getInputProps('code')}
        />
      )}

      <Button
        fullWidth
        mt="xl"
        size="lg"
        type="submit"
        loading={request.isLoading}
      >
        <Trans>Зарегистрироваться</Trans>
      </Button>
    </form>
  );
});
